import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Globe, MessageSquareText, CheckCircle2, CalendarCheck, Database, TrendingUp, Repeat } from "lucide-react";
import usePageSeo, { useBreadcrumbSchema } from "@/hooks/usePageSeo";
import { PAGE_SEO } from "@/data/pageSeo";
import { RevShell, ModuleBar, RevHero, SectionHead, RevCTA, MODULES, AGENTS, BrowserFrame, StatChip } from "./_shared";

const LOOP = [
  { icon: Globe, t: "Attract the visitor", d: "The website you already have (or one we build) brings people in." },
  { icon: MessageSquareText, t: "Answer the inquiry", d: "Ava replies on chat or by phone, 24/7, in their language." },
  { icon: CheckCircle2, t: "Qualify the opportunity", d: "Your questions, scoring and routing — applied consistently." },
  { icon: CalendarCheck, t: "Book the appointment", d: "Straight into your calendar, with reminders and rescheduling." },
  { icon: Repeat, t: "Follow up on approved channels", d: "Relay nurtures across email, SMS and WhatsApp — consent-first." },
  { icon: Database, t: "Update the CRM", d: "Every touch lands on one timeline and syncs to your CRM." },
  { icon: TrendingUp, t: "Show what produced revenue", d: "Attribution from first visit to closed deal." },
];

function FlywheelVisual() {
  return (
    <BrowserFrame url="app.websitetech.pro/revenue-center" data-testid="rev-flywheel">
      <div className="space-y-2.5">
        {LOOP.slice(0, 5).map((s, i) => (
          <div key={s.t} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5">
            <div className="h-8 w-8 rounded-lg bg-brand/10 grid place-items-center text-brand shrink-0"><s.icon size={16} /></div>
            <div className="text-sm font-semibold text-slate-700">{s.t}</div>
            <div className="ml-auto text-[10px] font-bold text-emerald-600 bg-emerald-50 rounded-full px-2 py-0.5">live</div>
          </div>
        ))}
        <div className="flex items-center gap-2 pt-1 text-xs text-slate-400"><Repeat size={13} /> Continuously improving from every result</div>
      </div>
    </BrowserFrame>
  );
}

export default function RevenueCenter() {
  usePageSeo(PAGE_SEO["/ai-revenue-center"]);
  useBreadcrumbSchema([{ name: "Home", url: "/" }, { name: "Growth Platform", url: "/tools" }, { name: "AI Revenue Center", url: "/ai-revenue-center" }]);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <RevShell testid="revenue-center-page">
      <RevHero
        eyebrow="Flagship · AI Revenue Center™"
        title="From website visitor to qualified customer —"
        accent="automatically."
        sub="Your scattered tools, working as one system. WebsiteTech builds the website, answers the inquiry, qualifies the opportunity, books the appointment, follows up across approved channels, updates your CRM — and shows which activity produced revenue."
        visual={<FlywheelVisual />}
      />
      <ModuleBar />

      {/* Closed loop */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="rev-loop">
        <SectionHead eyebrow="One closed loop" title="Not a menu of services — a system" sub="Every step feeds the next, and results feed back into better targeting. That's the difference between a toolbox and an operating system for revenue." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {LOOP.map((s, i) => (
            <motion.div key={s.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="bg-white border border-slate-200 rounded-2xl card-shadow p-5">
              <div className="flex items-center gap-2 text-[11px] font-bold text-slate-300">STEP {i + 1}</div>
              <div className="h-10 w-10 rounded-xl bg-brand/10 grid place-items-center text-brand mt-2"><s.icon size={18} /></div>
              <div className="font-heading font-bold text-slate-900 mt-3">{s.t}</div>
              <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Named agents */}
      <section className="py-16 bg-white border-y border-slate-100" data-testid="rev-agents">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Your AI teammates" title="Three coordinated agents, one shared timeline" sub="Named, job-specific agents that hand off to each other — and to your team — without dropping context." />
          <div className="grid md:grid-cols-3 gap-5 mt-8">
            {AGENTS.map((a) => (
              <Link key={a.name} to={a.to} data-testid={`rev-agent-${a.name.toLowerCase()}`}
                className="group rounded-2xl border border-slate-200 bg-white card-shadow p-6 hover:-translate-y-1 transition-transform">
                <div className="h-12 w-12 rounded-2xl grid place-items-center text-white" style={{ background: a.color }}><a.icon size={22} /></div>
                <div className="mt-4 flex items-baseline gap-2">
                  <h3 className="font-heading text-xl font-extrabold text-slate-900">{a.name}</h3>
                  <span className="text-[11px] font-semibold text-slate-400">{a.tag}</span>
                </div>
                <div className="text-sm font-semibold text-brand">{a.role}</div>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{a.line}</p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-700 group-hover:text-brand mt-4">Meet {a.name} <ArrowRight size={14} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Illustrative outcome band */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <SectionHead eyebrow="What good looks like" title="Every activity, tied to revenue" sub="Illustrative dashboard — your real numbers replace these once you're live." />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          <StatChip label="Time to first response" value="18s" tone="emerald" />
          <StatChip label="Qualified-lead rate" value="41%" tone="brand" />
          <StatChip label="Meetings booked / mo" value="63" tone="slate" />
          <StatChip label="No-show rate" value="9%" tone="amber" />
        </div>
        <p className="text-xs text-slate-400 mt-4">Figures shown are illustrative examples, not guarantees.</p>
      </section>

      {/* Module grid */}
      <section className="py-16 bg-white border-y border-slate-100" data-testid="rev-modules">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Explore the system" title="Every part of the Revenue Center" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {MODULES.map((m) => (
              <Link key={m.slug} to={`/ai-revenue-center/${m.slug}`} data-testid={`rev-module-${m.slug}`}
                className="group rounded-2xl border border-slate-200 bg-white card-shadow p-5 hover:-translate-y-1 transition-transform">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-brand/10 grid place-items-center text-brand"><m.icon size={18} /></div>
                  <div>
                    <div className="font-heading font-bold text-slate-900 leading-tight">{m.name}</div>
                    {m.agent && <div className="text-[11px] font-semibold text-brand">Agent: {m.agent}</div>}
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-3 leading-relaxed">{m.blurb}</p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 group-hover:text-brand mt-3">Learn more <ArrowRight size={13} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Deployment */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-slate-200 bg-white card-shadow p-6">
            <div className="text-xs font-bold uppercase tracking-wide text-brand">Built in</div>
            <h3 className="font-heading text-xl font-bold text-slate-900 mt-2">On every WebsiteTech site</h3>
            <p className="text-sm text-slate-500 mt-2">New sites we build ship with the Revenue Center wired into forms, chat, calendar and CRM from day one.</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white card-shadow p-6">
            <div className="text-xs font-bold uppercase tracking-wide text-brand">Connected</div>
            <h3 className="font-heading text-xl font-bold text-slate-900 mt-2">On the site you already have</h3>
            <p className="text-sm text-slate-500 mt-2">Already have a WordPress, Wix or Shopify site? Connect the Revenue Center with a snippet — no rebuild required.</p>
          </div>
        </div>
      </section>

      <RevCTA title="See it work on your website." sub="Start with a free audit. We'll show you exactly where visitors are slipping away — and how the Revenue Center closes the loop." />
    </RevShell>
  );
}
